import { useEffect, useId, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ia as apiIa, type PedidoDeModelo, type Provedor, type ProvedorDisponivel } from '../api/ia'
import { ErroApi } from '../api/client'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import './regras.css'

const PROVEDORES: Provedor[] = ['OpenAi', 'Anthropic', 'Google']

/**
 * Rótulo ligado ao campo por `htmlFor`, e não por envolvimento.
 *
 * 🔴 O `<label>` que envolve o controle empurra o **valor** dele para dentro do
 * nome acessível: o campo "Modelo" passa a se chamar "Modelo gpt-4o-mini", e o
 * "Provedor" muda de nome conforme a opção escolhida. Quem usa leitor de tela
 * ouve o rótulo mudar sozinho — e o teste que procura o campo pelo rótulo não o
 * acha, que foi como isto apareceu.
 */
function CampoDoModelo({ rotulo, children }: { rotulo: string; children: (id: string) => ReactNode }) {
  const id = useId()

  return (
    <div className="modelo__campo">
      <label className="modelo__rotulo" htmlFor={id}>
        {rotulo}
      </label>
      {children(id)}
    </div>
  )
}

/**
 * Provedor e modelo de um assistente — os **seis** campos.
 *
 * 🔴 Meia tela aqui foi cortada da 036 às 08h05 de 07/08, e o motivo era o
 * tempo até a reunião das 10h. `PedidoDeModelo` tem seis campos — provedor,
 * modelo, provedor de reserva, modelo de reserva, teto por conversa, teto por
 * dia — e **teto de custo preenchido pela metade é limite que não limita**, com
 * a conta da OpenAI na mão do cliente. Volta inteira.
 *
 * Até aqui, `Provedor` e `Modelo` eram o que nascera no cadastro
 * (`gpt-4o-mini`), e trocar exigia `UPDATE` na tabela `assistente`.
 */
export function ModeloDoAssistente({
  assistenteId,
  provedorAtual,
  modeloAtual,
  aoSalvar,
}: {
  assistenteId: number
  provedorAtual: string
  modeloAtual: string
  aoSalvar?: () => void | Promise<void>
}) {
  const { t } = useTranslation()
  const { pode } = useSessao()

  // Escolher modelo continua sendo decisão de custo e comportamento para todos
  // os atendimentos: `ParametroCtgf`, não `ParametroGerenciar`.
  const editavel = pode('ParametroCtgf')

  const [provedores, setProvedores] = useState<ProvedorDisponivel[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [salvo, setSalvo] = useState(false)
  const [enviando, setEnviando] = useState(false)

  const [dados, setDados] = useState<PedidoDeModelo>({
    provedor: (provedorAtual as Provedor) ?? 'OpenAi',
    modelo: modeloAtual,
    provedorDeReserva: null,
    modeloDeReserva: null,
    tetoPorConversa: 0,
    tetoPorDia: 0,
  })

  useEffect(() => {
    apiIa.provedores().then(setProvedores).catch(() => setProvedores([]))
  }, [])

  if (!provedores) return <Estado tipo="carregando" />

  const temChave = (p: Provedor) => provedores.some((x) => x.provedor === p && x.configurado)

  const alterar = <C extends keyof PedidoDeModelo>(campo: C, valor: PedidoDeModelo[C]) => {
    setSalvo(false)
    setDados({ ...dados, [campo]: valor })
  }

  async function salvar() {
    setErro(null)
    setSalvo(false)
    setEnviando(true)

    try {
      const resultado = await apiIa.definirModelo(assistenteId, dados)

      setAviso(resultado.aviso)
      setSalvo(true)
      await aoSalvar?.()
    } catch (e) {
      const corpo = e instanceof ErroApi ? (e.corpo as { detail?: string } | null) : null
      setErro(corpo?.detail ?? t('comum.erroDescricao'))
    } finally {
      setEnviando(false)
    }
  }

  /**
   * 🔴 A reserva no mesmo provedor é permitida, e a tela **diz** o que ela não
   * cobre: credencial recusada e falta de saldo derrubam os dois ao mesmo
   * tempo, e é a falha mais provável. O aviso aparece antes de salvar, não
   * depois — depois já não é decisão, é notícia.
   */
  const reservaNoMesmo =
    dados.provedorDeReserva !== null && dados.provedorDeReserva === dados.provedor

  return (
    <div>
      {!editavel && <Aviso tom="info">{t('modelo.somenteLeitura')}</Aviso>}

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      {salvo && !erro && (
        <Aviso tom="sucesso">{t('modelo.salvo')}</Aviso>
      )}

      {reservaNoMesmo && <Aviso tom="alerta">{t('modelo.reservaNoMesmoProvedor')}</Aviso>}

      {aviso && salvo && <Aviso tom="alerta">{aviso}</Aviso>}

      <div className="modelo__grade">
        <EscolhaDeProvedor
          rotulo={t('modelo.provedor')}
          valor={dados.provedor}
          provedores={provedores}
          editavel={editavel}
          onChange={(p) => alterar('provedor', p!)}
        />

        <CampoDoModelo rotulo={t('modelo.modelo')}>
          {(id) => (
            <input
              id={id}
              value={dados.modelo}
              disabled={!editavel}
              onChange={(e) => alterar('modelo', e.target.value)}
            />
          )}
        </CampoDoModelo>

        <EscolhaDeProvedor
          rotulo={t('modelo.provedorDeReserva')}
          valor={dados.provedorDeReserva}
          provedores={provedores}
          editavel={editavel}
          permiteVazio
          onChange={(p) => {
            setSalvo(false)
            // Trocar a reserva por "nenhuma" leva o modelo dela junto: reserva
            // pela metade é pior que reserva nenhuma, e o servidor recusa.
            setDados({
              ...dados,
              provedorDeReserva: p,
              modeloDeReserva: p === null ? null : dados.modeloDeReserva,
            })
          }}
        />

        <CampoDoModelo rotulo={t('modelo.modeloDeReserva')}>
          {(id) => (
            <input
              id={id}
              value={dados.modeloDeReserva ?? ''}
              disabled={!editavel || dados.provedorDeReserva === null}
              onChange={(e) => alterar('modeloDeReserva', e.target.value || null)}
            />
          )}
        </CampoDoModelo>

        <CampoDoModelo rotulo={t('modelo.tetoPorConversa')}>
          {(id) => (
            <input
              id={id}
              type="number"
              step="0.01"
              min="0"
              value={dados.tetoPorConversa}
              disabled={!editavel}
              onChange={(e) => alterar('tetoPorConversa', Number(e.target.value))}
            />
          )}
        </CampoDoModelo>

        <CampoDoModelo rotulo={t('modelo.tetoPorDia')}>
          {(id) => (
            <input
              id={id}
              type="number"
              step="0.01"
              min="0"
              value={dados.tetoPorDia}
              disabled={!editavel}
              onChange={(e) => alterar('tetoPorDia', Number(e.target.value))}
            />
          )}
        </CampoDoModelo>
      </div>

      {!temChave(dados.provedor) && (
        <Aviso tom="perigo">{t('modelo.semChave', { provedor: dados.provedor })}</Aviso>
      )}

      {editavel && (
        <div className="modelo__acoes">
          <Botao
            variante="primario"
            carregando={enviando}
            disabled={!dados.modelo.trim() || !temChave(dados.provedor)}
            onClick={() => void salvar()}
          >
            {t('comum.salvar')}
          </Botao>
        </div>
      )}
    </div>
  )
}

/**
 * 🔴 Provedor sem chave aparece **desabilitado, com o motivo**.
 *
 * `GET /ia/provedores` já devolvia `configurado` por provedor exatamente para
 * isto, e nenhuma página o chamava. Oferecer um provedor sem chave é deixar o
 * portal prometer o que o servidor recusa — e o efeito, quando acontece, é
 * transbordo para humano com a causa escondida no banco. Foi o que aconteceu em
 * 07/08.
 */
function EscolhaDeProvedor({
  rotulo,
  valor,
  provedores,
  editavel,
  permiteVazio,
  onChange,
}: {
  rotulo: string
  valor: Provedor | null
  provedores: ProvedorDisponivel[]
  editavel: boolean
  permiteVazio?: boolean
  onChange: (provedor: Provedor | null) => void
}) {
  const { t } = useTranslation()

  return (
    <CampoDoModelo rotulo={rotulo}>
      {(id) => (
        <select
          id={id}
          value={valor ?? ''}
          disabled={!editavel}
          onChange={(e) => onChange((e.target.value || null) as Provedor | null)}
        >
          {permiteVazio && <option value="">{t('modelo.semReserva')}</option>}

          {PROVEDORES.map((p) => {
            const configurado = provedores.some((x) => x.provedor === p && x.configurado)

            return (
              <option key={p} value={p} disabled={!configurado}>
                {/* O motivo no próprio rótulo: opção cinza sem explicação faz
                    quem lê procurar defeito na tela. */}
                {configurado ? p : t('modelo.provedorSemChave', { provedor: p })}
              </option>
            )
          })}
        </select>
      )}
    </CampoDoModelo>
  )
}
